import type { LabelHTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type FieldProps = {
  label: string;
  htmlFor: string;
  /** Pictogramme devant le libellé : il donne sa nature au champ d'un coup d'œil. */
  icon?: ReactNode;
  error?: string;
  /** Texte d'aide affiché sous le champ quand il n'y a pas d'erreur. */
  hint?: string;
  /** Élément aligné à droite du label (lien « mot de passe oublié ? »…). */
  action?: ReactNode;
  /** Label lisible par les lecteurs d'écran uniquement (maquette sans label visible). */
  hiddenLabel?: boolean;
  children: ReactNode;
  className?: string;
} & Omit<LabelHTMLAttributes<HTMLLabelElement>, 'children' | 'htmlFor' | 'className'>;

export function Field({
  label,
  htmlFor,
  icon,
  error,
  hint,
  action,
  hiddenLabel = false,
  children,
  className,
  ...props
}: FieldProps) {
  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      <div className={cn('flex items-baseline justify-between gap-4', hiddenLabel && 'sr-only')}>
        {/* `items-center` quand il y a une icône : alignés sur la ligne de
            base, un pictogramme et son libellé se décalent d'un pixel. */}
        <label
          htmlFor={htmlFor}
          className={cn(
            'text-sm font-medium text-content',
            icon && 'inline-flex items-center gap-2',
          )}
          {...props}
        >
          {icon ? (
            <span aria-hidden="true" className="text-content-subtle [&>svg]:size-4">
              {icon}
            </span>
          ) : null}
          {label}
        </label>
        {action}
      </div>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="text-sm text-content-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
